import { Component, forwardRef, Input, OnChanges, SimpleChanges, ViewChild, inject } from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators} from '@angular/forms';
import {VwuiInput, VwuiTextarea} from '@bvgo-ict/vwui-angular';
import {ToastrService} from 'ngx-toastr';

@Component({
    selector: 'app-attribute-editable',
    templateUrl: './attribute-editable.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AttributeEditableComponent),
            multi: true
        }
    ],
    standalone: false
})
export class AttributeEditableComponent implements ControlValueAccessor, OnChanges {
    private toastr = inject(ToastrService);

    @Input() label: string | undefined;
    @Input() inputType!: 'text'|'number';
    @Input() valuePrefix: string | undefined
    @Input() valueSuffix: string | undefined;

    @ViewChild('inputElement') private inputElement: VwuiInput | VwuiTextarea | undefined;

    private onChange!: (value: string|number|undefined) => void;
    private onTouched!: () => void;

    editActive$ = new BehaviorSubject(false);

    value: string|number|undefined = undefined;
    valueControl = new FormControl();
    blurTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
    acceptTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
    disabled = false;

    ngOnChanges(changes: SimpleChanges) {
        const inputTypeChange = changes.inputType
        if (inputTypeChange.previousValue != inputTypeChange.currentValue) {
            if (inputTypeChange.currentValue === 'number') {
                this.valueControl.validator = Validators.max(Number.MAX_SAFE_INTEGER)
            } else if (inputTypeChange.currentValue === 'text') {
                this.valueControl.validator = Validators.maxLength(10000);
            } else {
                console.error('Unexpected input type', inputTypeChange.currentValue);
            }
        }
    }

    async setEditActive(editActive: boolean): Promise<void> {
        if (!this.disabled) {
            this.editActive$.next(editActive);

            if (this.inputElement) {
                if (editActive) {
                    setTimeout(() => this.inputElement && this.inputElement.focusField());
                }
            }
        }
    }

    onCancel(): void {
        this.setEditActive(false);
        this.valueControl.setValue(this.value);

        this.clearBlurTimeout();
    }

    onAccept(): void {
        clearTimeout(this.acceptTimeout);

        this.acceptTimeout = setTimeout(
            () => {
                if (!this.disabled && this.editActive$.value) {
                    this.clearBlurTimeout();
                    if (!this.valueControl.valid) {
                        if (this.valueControl.errors?.max) {
                            this.toastr.warning(`Het ingevulde getal is te groot, maximum is ${this.valueControl.errors?.max.max}`)
                        } else if (this.valueControl.errors?.maxlength) {

                            this.toastr.warning(`De ingevulde tekst is te lang, maximum aantal karakters is ${this.valueControl.errors?.maxlength.requiredLength}`);
                        }
                        else {
                            this.toastr.warning(`De ingevulde waarde is ongeldig`)
                        }
                        return;
                    }

                    this.setEditActive(false);


                    const value = this.value && this.inputType === 'number' ? +this.value : this.value;
                    const controlValue = this.inputType === 'number' ? +this.valueControl.value : this.valueControl.value;

                    if (value !== controlValue) {
                        this.value = controlValue;

                        if (this.onChange) {
                            this.onChange(this.value);
                        }
                    }
                }
            },
            200
        )
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    writeValue(value: string|number): void {
        this.value = value;
        this.valueControl.setValue(this.value);
    }

    onInputFocus(): void {
        this.setEditActive(true);
        this.onFocus();
    }

    /** Accept the value after the timeout. For example the user tabs to the next field without interacting with the buttons */
    onBlur(): void {
        this.blurTimeout = setTimeout(() => {
            this.onAccept();
        }, 1);
    }

    /** Clear the blur timeout. Element has focus and value must not be accepted */
    onFocus(): void {
        this.clearBlurTimeout();
    }

    private clearBlurTimeout(): void {
        clearTimeout(this.blurTimeout);
        this.blurTimeout = undefined;
    }
}
